import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorComponent } from '../../error/error.component';
import { BehaviorSubject, fromEvent, map, merge, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NetworkConnectivityService {
  private readonly onlineStatus$ = new BehaviorSubject<boolean>(navigator.onLine);
  private modalRef: LegacyAny = null;

  constructor(private readonly modalService: NgbModal) {
    this.initNetworkMonitoring();
  }

  /**
   * Initialize network monitoring by listening to browser online/offline events
   */
  private initNetworkMonitoring(): void {
    // Listen to online and offline events
    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).subscribe((isOnline: boolean) => {
      this.onlineStatus$.next(isOnline);
      
      if (!isOnline) {
        this.showNoInternetPopup();
      } else {
        // Close the modal if it's open when connection is restored
        this.closeNoInternetPopup();
      }
    });
  }

  /**
   * Get the current online status as an observable
   */
  public getOnlineStatus(): Observable<boolean> {
    return this.onlineStatus$.asObservable();
  }

  /**
   * Get the current online status value
   */
  public isOnline(): boolean {
    return this.onlineStatus$.value;
  }

  /**
   * Show the "No Internet Connection" error popup
   */
  public showNoInternetPopup(): void {
    // Only open modal if it's not already open
    if (!this.modalRef) {
      this.modalRef = this.modalService.open(ErrorComponent, {
        backdrop: 'static',
        keyboard: false,
        centered: true
      });

      this.modalRef.componentInstance.errorName = 'No internet connection';
      this.modalRef.componentInstance.errorDescription = 
        'Please check your internet connection to continue.';
      this.modalRef.componentInstance.confirmBtnText = 'Confirm';
      this.modalRef.componentInstance.showCancelButton = false;

      // Handle modal close
      this.modalRef.result.then(
        () => {
          this.modalRef = null;
          // Retry logic - check if connection is restored
          if (!navigator.onLine) {
            // If still offline, show the popup again
            setTimeout(() => this.showNoInternetPopup(), 500);
          }
        },
        () => {
          // Modal dismissed
          this.modalRef = null;
        }
      );
    }
  }

  /**
   * Close the "No Internet Connection" popup if it's open
   */
  public closeNoInternetPopup(): void {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
  }

  /**
   * Check if the error is a network-related error
   */
  public isNetworkError(error: LegacyAny): boolean {
    if (!error) {
      return false;
    }

    // Check for various network error indicators
    return (
      error.status === 0 || // Network failure
      error.status === 504 || // Gateway timeout
      error.status === 503 || // Service unavailable
      error.statusText === 'Unknown Error' ||
      error.message?.toLowerCase().includes('network') ||
      error.message?.toLowerCase().includes('connection') ||
      error.name === 'TimeoutError' ||
      error.name === 'HttpErrorResponse' && error.status === 0
    );
  }
}
